import React from "react";
import { EllipsisVertical, Library, Pencil, Trash2 } from "lucide-react";

import { useAgents } from "@/shared/api/agents";
import { useKbAgents } from "@/shared/api/knowledge";
import PixelAvatar from "@/shared/components/PixelAvatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { formatSize, plural } from "./format";

import type { KnowledgeBase } from "@/shared/types";

/* A base on the Knowledge home, as a card (grid) or a row (list). Its agents come from its
   links: one request per base (N+1) — fine for tens of bases; batch server-side past that. */

function useLinkedAgents(kbId: string) {
  const { data: links } = useKbAgents(kbId);
  const { data: agents } = useAgents();
  return (links ?? []).map((l) => agents?.find((a) => a.id === l.agent_id)).filter((a): a is NonNullable<typeof a> => !!a);
}

function BaseIcon({ base, small = false }: { base: KnowledgeBase; small?: boolean }) {
  return (
    <span className={`shrink-0 rounded-[10px] bg-[var(--surface-2)] flex items-center justify-center text-[var(--text-secondary)] ${small ? "w-8 h-8 text-[15px]" : "w-10 h-10 text-[18px]"}`}>
      {base.icon ?? <Library className={small ? "w-4 h-4" : "w-[18px] h-[18px]"} strokeWidth={1.75} />}
    </span>
  );
}

/** Avatars, then who they are: "Research Analyst", "Copy Editor +1". */
function UsedBy({ kbId }: { kbId: string }) {
  const agents = useLinkedAgents(kbId);
  if (!agents.length) return <span className="text-[12px] text-[var(--text-quaternary)] whitespace-nowrap">No agents</span>;
  return (
    <span className="min-w-0 flex items-center gap-1.5" title={agents.map((a) => a.name).join(", ")}>
      <span className="flex shrink-0">
        {agents.slice(0, 3).map((a, i) => (
          <span key={a.id} className={`rounded-full ring-2 ring-[var(--surface)] ${i ? "-ml-1.5" : ""}`}>
            <PixelAvatar seed={a.seed} hue={a.hue} size={18} />
          </span>
        ))}
      </span>
      <span className="min-w-0 truncate text-[12px] text-[var(--text-secondary)]">
        {agents[0].name}{agents.length > 1 ? ` +${agents.length - 1}` : ""}
      </span>
    </span>
  );
}

function BaseMenu({ base, onEdit, onDelete }: { base: KnowledgeBase; onEdit: () => void; onDelete: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`Actions for ${base.name}`}
          className="press w-7 h-7 shrink-0 rounded-[8px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
        >
          <EllipsisVertical className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40 p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu">
        {/* Deferred past Radix's close/focus-restore so the dialog opens cleanly. */}
        <DropdownMenuItem className="gap-2 rounded-[8px] text-[13px]" onSelect={() => setTimeout(onEdit, 0)}>
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} /> Rename
        </DropdownMenuItem>
        <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-[var(--hairline)]" />
        <DropdownMenuItem className="gap-2 rounded-[8px] text-[13px] text-[var(--danger)]" onSelect={() => setTimeout(onDelete, 0)}>
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type Props = { base: KnowledgeBase; onOpen: () => void; onEdit: () => void; onDelete: () => void };

export function KbCard({ base, onOpen, onEdit, onDelete }: Props) {
  return (
    <div className="relative min-w-0 rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift transition-shadow duration-200 hover:ring-[var(--hairline-strong)]">
      <button onClick={onOpen} className="press w-full h-full min-w-0 flex flex-col gap-3 p-4 text-left">
        <span className="flex items-start gap-3 min-w-0 pr-7">
          <BaseIcon base={base} />
          <span className="min-w-0">
            <span className="block text-[14px] font-medium tracking-[-0.005em] text-[var(--text-primary)] truncate" title={base.name}>{base.name}</span>
            <span className="block mt-0.5 text-[12.5px] leading-[1.45] text-[var(--text-tertiary)] line-clamp-2 min-h-[36px]">
              {base.description || "No description"}
            </span>
          </span>
        </span>
        <span className="mt-auto pt-3 border-t hairline flex items-center gap-2 min-w-0 text-[12px] text-[var(--text-tertiary)]">
          <span className="shrink-0 font-mono tabular-nums">{plural(base.file_count, "file")} · {formatSize(base.bytes)}</span>
          <span className="flex-1" />
          <UsedBy kbId={base.id} />
        </span>
      </button>
      <span className="absolute top-3 right-3"><BaseMenu base={base} onEdit={onEdit} onDelete={onDelete} /></span>
    </div>
  );
}

/** The list view's columns: name, files, storage, used by, actions. */
export const BASE_GRID = "grid grid-cols-[minmax(0,1fr)_auto_28px] md:grid-cols-[minmax(0,1fr)_72px_84px_minmax(0,180px)_28px] items-center gap-3";

export function KbListRow({ base, onOpen, onEdit, onDelete }: Props) {
  return (
    <div className={`${BASE_GRID} px-3 py-2.5 hover:bg-[var(--accent-hover)]`}>
      <button onClick={onOpen} className="press min-w-0 flex items-center gap-3 text-left">
        <BaseIcon base={base} small />
        <span className="min-w-0">
          <span className="block truncate text-[13.5px] font-medium text-[var(--text-primary)]">{base.name}</span>
          <span className="block truncate text-[12px] text-[var(--text-tertiary)]">{base.description || "No description"}</span>
        </span>
      </button>
      <span className="hidden md:block text-right text-[12px] font-mono tabular-nums text-[var(--text-secondary)]">{base.file_count.toLocaleString("en-US")}</span>
      <span className="text-right text-[12px] font-mono tabular-nums text-[var(--text-secondary)] whitespace-nowrap">{formatSize(base.bytes)}</span>
      <span className="hidden md:flex min-w-0"><UsedBy kbId={base.id} /></span>
      <BaseMenu base={base} onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}
